/**
 * Format document_period for display.
 *
 * All document types use quarterly accounting closes as period:
 *   mar2025 → "1T 2025", jun2025 → "2T 2025", sep2025 → "3T 2025", dic2024 → "4T 2024"
 *
 * Unknown formats — returned as-is.
 */

const QUARTER_MAP: Record<string, string> = {
  mar: '1T',
  jun: '2T',
  sep: '3T',
  dic: '4T',
};

/**
 * Format a document_period for user display.
 *
 * @param period  - Raw period string from DB (e.g., "jun2025", "sep2025")
 * @param docType - Document type (kept for API compatibility, no longer affects output)
 * @returns Formatted string (e.g., "2T 2025", "3T 2025")
 */
export function formatPeriod(period?: string, docType?: string): string {
  if (!period) return '';

  // Try to parse as quarterly close: {month_abbrev}{year}
  const match = period.match(/^(mar|jun|sep|dic)(\d{4})$/i);
  if (!match) return period;

  const monthKey = match[1].toLowerCase();
  const year = match[2];
  const quarter = QUARTER_MAP[monthKey];

  return quarter ? `${quarter} ${year}` : period;
}

/**
 * Month abbreviation map for humanizing non-quarterly periods.
 * Spanish 3-letter abbreviations → capitalized display form.
 */
const MONTH_DISPLAY: Record<string, string> = {
  ene: 'Ene',
  feb: 'Feb',
  mar: 'Mar',
  abr: 'Abr',
  may: 'May',
  jun: 'Jun',
  jul: 'Jul',
  ago: 'Ago',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dic: 'Dic',
};

/**
 * Format a period for filter UI (dropdown & chips) where docType is unknown.
 *
 * Quarterly close months → trimestral label:
 *   mar2025 → "1T 2025", jun2025 → "2T 2025", sep2025 → "3T 2025", dic2024 → "4T 2024"
 *
 * Other months (HE publication months) → humanized:
 *   oct2025 → "Oct 2025", feb2026 → "Feb 2026"
 *
 * Unknown formats → returned as-is.
 */
export function formatPeriodForFilter(period?: string): string {
  if (!period) return '';

  const match = period.match(/^([a-z]{3})(\d{4})$/i);
  if (!match) return period;

  const monthKey = match[1].toLowerCase();
  const year = match[2];

  // Quarterly close months → "1T 2025"
  const quarter = QUARTER_MAP[monthKey];
  if (quarter) return `${quarter} ${year}`;

  // Non-quarterly months → "Oct 2025"
  const monthDisplay = MONTH_DISPLAY[monthKey];
  if (monthDisplay) return `${monthDisplay} ${year}`;

  return period;
}

/**
 * Format period for citation display: "EMPRESA - TIPO (período)"
 * Convenience wrapper that formats the period portion in parentheses.
 */
export function formatPeriodForCitation(period?: string, docType?: string): string {
  const formatted = formatPeriod(period, docType);
  return formatted ? ` (${formatted})` : '';
}
